import 'react';
import classnames from 'classnames';
import styled, { css } from 'styled-components';
import { selectFromMap, selectFromProps, selectFromTheme } from '@thisisbud/sc-utils';
import { jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4;
const inlineLinkClassName = 'bui-inline-link';
const inlineLinkCommonCss = /*#__PURE__*/css(_t || (_t = _`
  display: inline;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  text-transform: inherit;
  cursor: pointer;
`));

/**
 * Get the CSS for a specific inline link variant.
 *
 * @param variant - The inline link style variant
 */
function getInlineLinkCss(variant) {
  return css(_t2 || (_t2 = _`
    color: ${0};
    text-decoration: ${0};

    &:hover {
      color: ${0};
      text-decoration: ${0};
    }

    &:active {
      color: ${0};
      text-decoration: ${0};
    }
  `), selectFromTheme(`colorTextInlineLink${variant}Default`), selectFromTheme(`textDecorationInlineLink${variant}Default`), selectFromTheme(`colorTextLink${variant}Hover`), selectFromTheme(`textDecorationInlineLink${variant}Hover`), selectFromTheme(`colorTextLink${variant}Active`), selectFromTheme(`textDecorationInlineLink${variant}Active`));
}
const inlineLinkPrimaryCss = getInlineLinkCss('Primary');
const inlineLinkSecondaryCss = getInlineLinkCss('Secondary');

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledAnchorInlineLink = /*#__PURE__*/styled.a.withConfig({
  displayName: "StyledAnchorInlineLink",
  componentId: "sc-c4uchi-0"
})(_t3 || (_t3 = _`
  ${0}
  ${0}
`), inlineLinkCommonCss, selectFromMap(selectFromProps('$variant'), {
  primary: inlineLinkPrimaryCss,
  secondary: inlineLinkSecondaryCss
}));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledButtonInlineLink = /*#__PURE__*/styled.button.withConfig({
  displayName: "StyledButtonInlineLink",
  componentId: "sc-c4uchi-1"
})(_t4 || (_t4 = _`
  ${0}
  ${0}
  background-color: transparent;
  padding: 0;
  border: 0;
  box-sizing: border-box;
  appearance: none;
  cursor: pointer;
`), inlineLinkCommonCss, selectFromMap(selectFromProps('$variant'), {
  primary: inlineLinkPrimaryCss,
  secondary: inlineLinkSecondaryCss
}));

/**
 * Render the InlineLink component as an `a` tag.
 *
 * @param props - The component props
 * @param className - The class name of the root element
 */
function renderAnchor(props, className) {
  const {
    children,
    download,
    href,
    id,
    onClick,
    rel,
    target,
    variant
  } = props;
  return /*#__PURE__*/jsx(StyledAnchorInlineLink, {
    $variant: variant,
    className: className,
    download: download,
    href: href,
    id: id,
    rel: rel,
    tabIndex: 0,
    target: target,
    onClick: onClick,
    children: children
  });
}

/**
 * Render the InlineLink component as an `button` tag.
 *
 * @param props - The component props
 * @param className - The class name of the root element
 */
function renderButton(props, className) {
  const {
    children,
    disabled,
    id,
    onClick,
    value,
    variant
  } = props;
  return /*#__PURE__*/jsx(StyledButtonInlineLink, {
    $variant: variant,
    className: className,
    disabled: disabled,
    id: id,
    tabIndex: 0,
    type: "button",
    value: value,
    onClick: onClick,
    children: children
  });
}

/**
 * A component that displays a link to be wrapped inside a block element
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { InlineLink } from '@thisisbud/bui-react-inline-link'
 *
 * function Example() {
 *   return <InlineLink href='https://www.thisisbud.com' target='_blank'>Visit Bud's website</InlineLink>
 * }
 * ```
 */
function InlineLink(props) {
  const {
    className,
    type,
    variant
  } = props;
  const computedClassName = classnames(inlineLinkClassName, `${inlineLinkClassName}--${variant}`, className);
  return type === 'button' ? renderButton(props, computedClassName) : renderAnchor(props, computedClassName);
}
InlineLink.defaultProps = {
  type: 'anchor',
  variant: 'primary'
};
InlineLink.displayName = 'InlineLink';

export { InlineLink, inlineLinkClassName };
